'use client'

import {useEffect, useRef, useState} from 'react'
import {tv, type VariantProps} from 'tailwind-variants'
import {type OmitStrict} from 'type-zoo'
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from '~/design-system/foundations/Table'
import {
  HgTableBodyCell,
  type HgTableBodyCellType,
} from '~/design-system/hg/components'
import {
  HgTableCollapsibleSection,
  HgTableTitleCell,
} from '~/design-system/hg/components/HgTable'
import {
  type HgTableCollapsibleSectionProps,
  type HgTableTitleCellType,
} from '~/design-system/hg/components/HgTable/types'

type StandardTableCollapsibleSectionProps = OmitStrict<
  HgTableCollapsibleSectionProps,
  'children'
> & {
  type: 'collapsible'
  rows: StandardTableRow[]
}

type StandardTableRowProps = {
  type: 'default'
  rows: StandardTableRow[]
}

export type StandardTableTitleRow = [
  HgTableTitleCellType,
  HgTableTitleCellType,
  HgTableTitleCellType?,
  HgTableTitleCellType?,
  HgTableTitleCellType?,
  HgTableTitleCellType?,
]

export type StandardTableRow = [
  HgTableBodyCellType,
  HgTableBodyCellType,
  HgTableBodyCellType?,
  HgTableBodyCellType?,
  HgTableBodyCellType?,
  HgTableBodyCellType?,
]

export type StandardTableBodySection =
  | StandardTableRowProps
  | StandardTableCollapsibleSectionProps

type StandardTableVariantProps = VariantProps<typeof standardTableVariants>

export type StandardTableProps = {
  columnTitles?: StandardTableTitleRow | null
  sections: StandardTableBodySection[]
  className?: string
} & StandardTableVariantProps

export const standardTableVariants = tv({
  slots: {
    wrapper:
      'relative border-border-default after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:w-[200px] after:bg-gradient-to-l after:from-background-default after:transition-opacity data-[scroll=false]:after:opacity-0 ',
    cell: 'min-w-[156px] py-s3 pr-s3',
    cellContent: 'items-start',
    tableRow:
      'grid grid-flow-col grid-cols-[repeat(auto-fit,minmax(156px,1fr))] gap-x-grid-column-gap',
  },
  variants: {
    lastColumnAlignment: {
      left: '',
      right: {
        wrapper:
          '[&_td:last-of-type>*]:justify-end [&_td:last-of-type]:text-right [&_th:last-of-type>*]:justify-end [&_th:last-of-type]:text-right',
      },
    },
    numColumns: {
      2: '',
      3: '',
      4: '',
      5: '',
      6: '',
    },
    asymmetrical: {
      true: '',
    },
  },
  compoundVariants: [
    {
      numColumns: 2,
      asymmetrical: true,
      class: {tableRow: 'grid-cols-[2fr_1fr] md:grid-cols-[5fr_3fr]'},
    },
    {
      numColumns: 3,
      asymmetrical: true,
      class: {tableRow: 'grid-cols-[3fr_2fr_2fr] md:grid-cols-[2fr_1fr_1fr]'},
    },
    {
      numColumns: 4,
      asymmetrical: true,
      class: {
        tableRow: 'grid-cols-[3fr_2fr_2fr_2fr] md:grid-cols-[3fr_1fr_1fr_1fr]',
      },
    },
  ],
})

const StandardTableBodyRows = ({
  rows,
  classNames,
  lastColumnRef,
}: {
  rows: StandardTableRow[]
  classNames: {
    tableRow: string
    cell: string
    cellContent: string
  }
  lastColumnRef: React.RefObject<HTMLTableCellElement | null>
}) => {
  return rows.map((column, rowIndex) => (
    <TableRow key={rowIndex} className={classNames.tableRow}>
      {column.map(
        (bodyCell, columnIndex) =>
          bodyCell && (
            <HgTableBodyCell
              {...bodyCell}
              key={columnIndex}
              classNames={{cell: classNames.cell, content: classNames.cellContent}}
              alignment="start"
              ref={
                columnIndex === column.length - 1 && rowIndex === rows.length - 1
                  ? lastColumnRef
                  : null
              }
            />
          )
      )}
    </TableRow>
  ))
}

export default function StandardTable({
  columnTitles,
  lastColumnAlignment,
  sections,
  asymmetrical = false,
  className,
}: StandardTableProps) {
  const lastColumnRef = useRef<HTMLTableCellElement>(null)
  const wrapperRef = useRef<HTMLTableElement | null>(null)
  const [hasScroll, setHasScroll] = useState(false)

  const {wrapper, cell, cellContent, tableRow} = standardTableVariants({
    lastColumnAlignment,
    numColumns: sections[0]?.rows[0]?.length,
    asymmetrical,
  })

  useEffect(() => {
    if (!lastColumnRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHasScroll(!entry.isIntersecting)
      },
      {threshold: 0.6, root: wrapperRef.current}
    )
    observer.observe(lastColumnRef.current)
    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div
      className={wrapper({class: className})}
      data-scroll={hasScroll}
      ref={wrapperRef}
    >
      <Table>
        <TableHeader className="text-text-emphasized arcadia-heading-9">
          {columnTitles && (
            <TableRow className={tableRow()}>
              {columnTitles.map(
                (column, index) =>
                  column && (
                    <HgTableTitleCell className={cell()} key={index} {...column} />
                  )
              )}
            </TableRow>
          )}
        </TableHeader>
        {sections.map((section, index) => {
          if (section.type === 'collapsible') {
            return (
              <HgTableCollapsibleSection
                id={section.id}
                key={index}
                icon={section.icon}
                heading={section.heading}
              >
                <StandardTableBodyRows
                  classNames={{
                    tableRow: tableRow(),
                    cell: cell(),
                    cellContent: cellContent(),
                  }}
                  rows={section.rows}
                  lastColumnRef={lastColumnRef}
                />
              </HgTableCollapsibleSection>
            )
          }
          return (
            <TableBody key={index} className="text-text-default arcadia-body-2">
              <StandardTableBodyRows
                classNames={{
                  tableRow: tableRow(),
                  cell: cell(),
                  cellContent: cellContent(),
                }}
                rows={section.rows}
                lastColumnRef={lastColumnRef}
              />
            </TableBody>
          )
        })}
      </Table>
    </div>
  )
}
